import styled, { CSSObject } from '@emotion/styled';
import merge from 'lodash.merge';
import {
  BLACK,
  BLACK_05,
  BLACK_30,
  BLACK_50,
  GREEN,
  GREEN_10,
  GREEN_DARK,
  PURPLE,
  PURPLE_20,
  RED,
  RED_10,
  WHITE,
} from '@/ui-kit/theme';
import { Loader } from '@/ui-kit/loader';
import {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
} from './constants';

interface IStyledButtonProps {
  color: ButtonColor;
  size: ButtonSize;
  variant: ButtonVariant;
  isLoading: boolean;
}

export const StyledButton = styled.button<IStyledButtonProps>(({
  color, size, variant, isLoading,
}) => {
  let styles: CSSObject = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    outline: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
    flexShrink: 0,
    gap: '8px',

    svg: { flexShrink: 0 },
  };

  if (color === ButtonColor.PRIMARY) {
    const primaryStyles: CSSObject = {
      color: WHITE,
      boxShadow: '0px 1px 2px rgba(10, 11, 12, 0.1)',
      backgroundColor: GREEN,
      ':hover': { backgroundColor: GREEN_DARK },
      ':active': { backgroundColor: GREEN_DARK },
      ':disabled': {
        backgroundColor: GREEN,
        opacity: 0.3,
        cursor: 'default',
      },
    };

    styles = merge(styles, primaryStyles);
  }

  if (color === ButtonColor.SECONDARY) {
    const secondaryStyles: CSSObject = {
      color: BLACK,
      boxShadow: '0px 1px 2px rgba(10, 11, 12, 0.1), inset 0px 0px 0px 1px rgba(10, 11, 12, 0.1)',
      backgroundColor: WHITE,
      ':hover': { backgroundColor: BLACK_05 },
      ':active': { backgroundColor: BLACK_05 },
      ':disabled': {
        backgroundColor: WHITE,
        opacity: 0.3,
        cursor: 'default',
      },
    };

    styles = merge(styles, secondaryStyles);
  }

  if (color === ButtonColor.ACTION) {
    const actionStyles: CSSObject = {
      color: BLACK,
      boxShadow: '0px 1px 2px rgba(10, 11, 12, 0.1)',
      backgroundColor: WHITE,
      border: `2px dashed ${GREEN}`,
      ':hover': { backgroundColor: GREEN_10 },
      ':active': { backgroundColor: GREEN_10 },
      ':disabled': {
        backgroundColor: WHITE,
        opacity: 0.3,
        cursor: 'default',
      },
    };

    styles = merge(styles, actionStyles);
  }

  if (color === ButtonColor.ACTION_AI) {
    const actionAiStyles: CSSObject = {
      color: BLACK,
      boxShadow: '0px 1px 2px rgba(10, 11, 12, 0.1)',
      backgroundColor: WHITE,
      border: `2px dashed ${PURPLE}`,
      ':hover': { backgroundColor: PURPLE_20 },
      ':active': { backgroundColor: PURPLE_20 },
      ':disabled': {
        backgroundColor: WHITE,
        opacity: 0.3,
        cursor: 'default',
      },
      svg: { color: PURPLE },
    };

    styles = merge(styles, actionAiStyles);
  }

  if (color === ButtonColor.GHOST) {
    const ghostStyles: CSSObject = {
      color: BLACK,
      backgroundColor: 'transparent',
      ':hover': { backgroundColor: BLACK_05 },
      ':active': { backgroundColor: BLACK_05 },
      ':disabled': {
        backgroundColor: 'transparent',
        opacity: 0.3,
        cursor: 'default',
      },
    };

    styles = merge(styles, ghostStyles);
  }

  if (color === ButtonColor.GHOST_SECONDARY) {
    const ghostStyles: CSSObject = {
      color: BLACK_30,
      backgroundColor: 'transparent',
      ':hover': {
        backgroundColor: BLACK_05,
        color: BLACK,
      },
      ':active': {
        backgroundColor: BLACK_05,
        color: BLACK,
      },
      ':disabled': {
        color: BLACK_30,
        backgroundColor: 'transparent',
        opacity: 0.3,
        cursor: 'default',
      },
    };

    styles = merge(styles, ghostStyles);
  }

  if (color === ButtonColor.DANGER) {
    const dangerStyles: CSSObject = {
      color: BLACK_50,
      backgroundColor: WHITE,
      boxShadow: '0px 1px 2px rgba(10, 11, 12, 0.1), inset 0px 0px 0px 1px rgba(10, 11, 12, 0.1)',
      ':hover': {
        color: RED,
        backgroundColor: RED_10,
      },
      ':active': {
        color: RED,
        backgroundColor: RED_10,
      },
      ':disabled': {
        color: BLACK_50,
        backgroundColor: WHITE,
        opacity: 0.3,
        cursor: 'default',
      },
    };
    styles = merge(styles, dangerStyles);
  }

  if (size === ButtonSize.SMALL) {
    const smallStyles: CSSObject = {
      borderRadius: '10px',
      padding: '0 16px',
      height: '32px',
      fontSize: '14px',
      fontWeight: '600',
      lineHeight: '1.5em',
    };

    styles = merge(styles, smallStyles);
  }

  if (size === ButtonSize.MEDIUM) {
    const mediumStyles: CSSObject = {
      borderRadius: '12px',
      padding: '0 24px',
      height: '36px',
      fontSize: '14px',
      fontWeight: '600',
      lineHeight: '1.5em',
    };

    styles = merge(styles, mediumStyles);
  }

  if (size === ButtonSize.LARGE) {
    const largeStyles: CSSObject = {
      borderRadius: '12px',
      padding: '0 24px',
      height: '40px',
      fontSize: '16px',
      fontWeight: '600',
      lineHeight: '1.5em',
    };

    styles = merge(styles, largeStyles);
  }

  if (variant === ButtonVariant.ICON_ATTACHED && size === ButtonSize.SMALL) {
    const smallWithIconStyles: CSSObject = {
      paddingLeft: '12px',
      paddingRight: '12px',
    };

    styles = merge(styles, smallWithIconStyles);
  }

  if (variant === ButtonVariant.ICON_ATTACHED && size === ButtonSize.MEDIUM) {
    const mediumWithIconStyles: CSSObject = {
      paddingLeft: '12px',
      paddingRight: '12px',
    };

    styles = merge(styles, mediumWithIconStyles);
  }

  if (variant === ButtonVariant.ICON_ATTACHED && size === ButtonSize.LARGE) {
    const largeWithIconStyles: CSSObject = {
      paddingLeft: '16px',
      paddingRight: '16px',
    };

    styles = merge(styles, largeWithIconStyles);
  }

  if (variant === ButtonVariant.ONLY_ICON && size === ButtonSize.LARGE) {
    const largeOnlyIconStyles: CSSObject = {
      padding: 0,
      width: '40px',
      height: '40px',
    };

    styles = merge(styles, largeOnlyIconStyles);
  }

  if (variant === ButtonVariant.ONLY_ICON && size === ButtonSize.MEDIUM) {
    const largeOnlyIconStyles: CSSObject = {
      padding: 0,
      width: '37px',
      height: '37px',
    };

    styles = merge(styles, largeOnlyIconStyles);
  }

  if (variant === ButtonVariant.ONLY_ICON && size === ButtonSize.SMALL) {
    const largeOnlyIconStyles: CSSObject = {
      padding: 0,
      width: '32px',
      height: '32px',
    };

    styles = merge(styles, largeOnlyIconStyles);
  }

  if (isLoading) {
    const isLoadingStyles: CSSObject = { cursor: 'wait' };
    styles = merge(styles, isLoadingStyles);
  }

  return styles;
});

export const StyledLoader = styled(Loader)(() => {
  return {
    width: '20px',
    height: '20px',
  };
});
